// features/home/NewPostsPill.tsx — "N new posts" pill driven by realtime `feed.new`.
//
// Renders only while feedPillStore has pending ids. Clicking it re-fetches the list
// the active tab renders from, then clears the store.

import styled from 'styled-components';
import { Icon } from '@/icons';
import { useFeedPillStore } from '@/core/stores/feedPillStore';

const Pill = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  align-self: center;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.xs};
  cursor: pointer;
  transition: background 120ms ease;
`;

export interface NewPostsPillProps {
  /**
   * Refetch the first page of whichever list the active Home tab shows. Every tab
   * renders from its own server-picked id list (feedStore / forYouStore), so the page
   * owns the refetch: a pill that reloaded some other list looked present but did
   * nothing (W5, 1.21.00 review).
   */
  onRefetch: () => Promise<void>;
}

export function NewPostsPill({ onRefetch }: NewPostsPillProps) {
  const newPostIds = useFeedPillStore((s) => s.newPostIds);
  const clear = useFeedPillStore((s) => s.clear);
  const count = newPostIds.length;

  function onClick() {
    void onRefetch()
      .catch(() => {})
      .finally(() => clear());
  }

  // W13 (1.21.00 review): the live region itself must already be present in
  // the DOM for a screen reader to announce a LATER content change into it —
  // mounting a brand-new aria-live element at the same moment its content
  // first appears does not reliably announce. So this wrapper always renders
  // (empty when there's nothing to say); only the button inside comes and goes.
  return (
    <div aria-live="polite">
      {count > 0 && (
        <Pill type="button" onClick={onClick}>
          <Icon name="arrow-up" size={14} />
          {count === 1 ? '1 new post' : `${count} new posts`}
        </Pill>
      )}
    </div>
  );
}
